import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  HrComplianceSettings,
  UpsertImmigrationComplianceData,
  immigrationComplianceApi,
} from '../immigration-compliance';

export const immigrationComplianceKeys = {
  all: ['immigration-compliance'] as const,
  mine: () => [...immigrationComplianceKeys.all, 'mine'] as const,
  adminList: (role?: string) => [...immigrationComplianceKeys.all, 'admin', 'list', role ?? null] as const,
  adminByUser: (userId?: string) => [...immigrationComplianceKeys.all, 'admin', 'by-user', userId ?? null] as const,
  settings: () => [...immigrationComplianceKeys.all, 'settings'] as const,
};

export function useMyImmigrationComplianceQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: immigrationComplianceKeys.mine(),
    queryFn: () => immigrationComplianceApi.getMine(),
    enabled,
  });
}

export function useAdminImmigrationComplianceListQuery(role?: 'staff' | 'student') {
  return useQuery({
    queryKey: immigrationComplianceKeys.adminList(role),
    queryFn: () => immigrationComplianceApi.adminList(role),
  });
}

export function useAdminImmigrationComplianceByUserQuery(userId?: string) {
  return useQuery({
    queryKey: immigrationComplianceKeys.adminByUser(userId),
    queryFn: () => immigrationComplianceApi.adminGetByUser(userId as string),
    enabled: Boolean(userId),
  });
}

export function useAdminUpsertImmigrationComplianceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpsertImmigrationComplianceData }) =>
      immigrationComplianceApi.adminUpsert(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: immigrationComplianceKeys.all });
    },
  });
}

export function useHrComplianceSettingsQuery() {
  return useQuery({
    queryKey: immigrationComplianceKeys.settings(),
    queryFn: () => immigrationComplianceApi.getSettings(),
  });
}

export function useUpdateHrComplianceSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: HrComplianceSettings) => immigrationComplianceApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: immigrationComplianceKeys.settings() });
    },
  });
}
